import {
  getDownloadUrl,
  listAttachments,
  presignUpload,
  putToStorage,
  registerUpload,
  softDeleteAttachment,
  type AttachmentHandle,
  type PerSlotPolicy,
} from '@/features/attachments';

export const MAX_PDF_BYTES = 10 * 1024 * 1024;
export const INVOICE_PDF_CATEGORY = 'invoice-pdf';

// Invoice PDFs are a multi-bucket entity (deliberately absent from the backend
// bucket map), so the policy carries the bucket label explicitly. The generic
// Files pipeline (presign → PUT → register) needs no per-entity backend wiring;
// the bucket is only a DB label (the S3 key is invoice/<id>/<uuid>.pdf).
export const INVOICE_PDF_POLICY: PerSlotPolicy = {
  acceptedTypes: ['application/pdf'],
  maxFileSize: MAX_PDF_BYTES,
  category: INVOICE_PDF_CATEGORY,
  bucket: 'invoice-documents',
};

export function invoicePdfAttachmentsKey(invoiceId: string) {
  return ['invoice-pdf-attachments', invoiceId] as const;
}

// One PDF per invoice: the newest upload is current; a replace soft-deletes the
// prior so only one row remains live.
// Upload registers the new PDF before soft-deleting the prior, so a failed
// soft-delete can leave two live rows. Pick the newest deterministically
// (created_at desc, tie-broken by id) rather than trusting the list's order.
export function newestInvoicePdfAttachment(
  list: AttachmentHandle[] | undefined,
): AttachmentHandle | null {
  if (list === undefined || list.length === 0) return null;
  return (
    [...list].sort((a, b) => {
      if (a.created_at !== b.created_at) return a.created_at < b.created_at ? 1 : -1;
      return a.attachment_id < b.attachment_id ? 1 : -1;
    })[0] ?? null
  );
}

// A short-lived download URL for the invoice's current uploaded PDF, or null
// when none is attached.
export async function fetchUploadedInvoicePdfUrl(invoiceId: string): Promise<string | null> {
  const attachment = newestInvoicePdfAttachment(
    await listAttachments({
      entityType: 'invoice',
      entityId: invoiceId,
      category: INVOICE_PDF_CATEGORY,
    }),
  );
  if (attachment === null) return null;
  return (await getDownloadUrl(attachment.file_id, 600)).url;
}

export function validatePdf(file: File): string | null {
  if (file.type !== 'application/pdf') return 'Only PDF files are accepted.';
  if (file.size > MAX_PDF_BYTES) return 'The PDF exceeds the 10 MB limit.';
  return null;
}

// Upload a PDF for an invoice through the Files pipeline. When replacing, the new
// row is registered before the prior is soft-deleted so a failure never leaves
// the invoice with no PDF.
export async function performInvoicePdfUpload(
  invoiceId: string,
  file: File,
  previous: AttachmentHandle | null,
): Promise<void> {
  const presigned = await presignUpload({
    entityType: 'invoice',
    entityId: invoiceId,
    category: INVOICE_PDF_CATEGORY,
    originalName: file.name,
    mimeType: file.type,
    sizeBytes: file.size,
    policy: INVOICE_PDF_POLICY,
  });
  await putToStorage(presigned.upload_url, file);
  await registerUpload(presigned.upload_handle);
  if (previous !== null) await softDeleteAttachment(previous.attachment_id);
}
